import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../hooks/transactions";
import type { Category, TransactionDirection } from "../lib/transactions";

const DIRECTIONS: TransactionDirection[] = ["INCOME", "EXPENSE", "TRANSFER"];

export function CategoriesList() {
  const { t } = useTranslation();
  const list = useCategories();
  const create = useCreateCategory();
  const update = useUpdateCategory();
  const remove = useDeleteCategory();

  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);

  if (list.isLoading) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const categories = list.data?.categories ?? [];

  const byDirection: Record<TransactionDirection, Category[]> = {
    INCOME: [],
    EXPENSE: [],
    TRANSFER: [],
  };
  for (const c of categories) byDirection[c.direction].push(c);
  // Sortierung: zuerst Roots, dann Children (alphabetisch innerhalb)
  for (const d of DIRECTIONS) {
    byDirection[d].sort((a, b) => a.fullPath.localeCompare(b.fullPath));
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_reports")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("categories.list.title")}
          </h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {t("categories.list.intro")}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setCreating(true);
          }}
          className="px-3 py-2 rounded-lg text-white text-sm font-medium"
          style={{ background: "var(--accent)" }}
        >
          + {t("categories.list.new")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {DIRECTIONS.map((d) => (
          <section key={d} className="fv-card p-5">
            <h2 className="font-semibold mb-2">{t(`categories.direction.${d}`)}</h2>
            {byDirection[d].length === 0 ? (
              <p className="text-xs text-slate-500">{t("categories.list.empty_direction")}</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {byDirection[d].map((c) => (
                  <li key={c.id} className="flex items-center justify-between group">
                    <span className="flex items-center gap-2">
                      {c.color && (
                        <span
                          className="inline-block w-3 h-3 rounded-full"
                          style={{ backgroundColor: c.color }}
                        />
                      )}
                      <span>
                        {c.fullPath}
                        {c.transactionCount > 0 && (
                          <span className="text-xs text-slate-500 ml-1">
                            ({c.transactionCount})
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="opacity-0 group-hover:opacity-100 flex gap-2 text-xs">
                      <button
                        type="button"
                        className="text-blue-600"
                        onClick={() => {
                          setCreating(false);
                          setEditing(c);
                        }}
                      >
                        {t("categories.list.edit")}
                      </button>
                      <button
                        type="button"
                        className="text-red-700"
                        onClick={() => {
                          if (
                            window.confirm(
                              c.transactionCount > 0
                                ? t("categories.list.confirm_delete_with_tx", {
                                    count: c.transactionCount,
                                  })
                                : t("categories.list.confirm_delete"),
                            )
                          ) {
                            remove.mutate(c.id);
                          }
                        }}
                      >
                        {t("categories.list.delete")}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {(creating || editing) && (
        <CategoryFormModal
          existing={editing}
          allCategories={categories}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSubmit={async (input) => {
            if (editing) await update.mutateAsync({ id: editing.id, input });
            else await create.mutateAsync(input);
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function CategoryFormModal({
  existing,
  allCategories,
  onSubmit,
  onCancel,
}: {
  existing: Category | null;
  allCategories: Category[];
  onSubmit: (input: {
    name: string;
    direction: TransactionDirection;
    parentId: string | null;
    color: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(existing?.name ?? "");
  const [direction, setDirection] = useState<TransactionDirection>(
    existing?.direction ?? "EXPENSE",
  );
  const [parentId, setParentId] = useState<string>(existing?.parentId ?? "");
  const [color, setColor] = useState(existing?.color ?? "#3b82f6");
  const [error, setError] = useState<string | undefined>();

  const possibleParents = allCategories.filter(
    (c) => c.direction === direction && c.id !== existing?.id,
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    try {
      await onSubmit({
        name: name.trim(),
        direction,
        parentId: parentId || null,
        color: color || null,
      });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded p-4 w-full max-w-md space-y-3 text-sm"
      >
        <h2 className="font-semibold">
          {existing ? t("categories.form.edit_title") : t("categories.form.new_title")}
        </h2>
        <label className="block">
          <span className="text-slate-600">{t("categories.form.name")}</span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border rounded px-2 py-1 w-full mt-0.5"
          />
        </label>
        <label className="block">
          <span className="text-slate-600">{t("categories.form.direction")}</span>
          <select
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value as TransactionDirection);
              setParentId("");
            }}
            className="border rounded px-2 py-1 w-full mt-0.5"
            disabled={
              !!existing &&
              (existing.transactionCount > 0 ||
                allCategories.some((c) => c.parentId === existing.id))
            }
          >
            {DIRECTIONS.map((d) => (
              <option key={d} value={d}>
                {t(`categories.direction.${d}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-600">{t("categories.form.parent")}</span>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            className="border rounded px-2 py-1 w-full mt-0.5"
          >
            <option value="">{t("categories.form.no_parent")}</option>
            {possibleParents.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullPath}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-600">{t("categories.form.color")}</span>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="border rounded h-8 w-16 mt-0.5"
          />
        </label>
        {error && <p className="text-red-700 text-xs">{error}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} className="px-3 py-1 border rounded">
            {t("categories.form.cancel")}
          </button>
          <button type="submit" className="px-3 py-1 bg-blue-600 text-white rounded">
            {t("categories.form.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
